/**
 * Thin ECharts wrapper: mount, set option, resize, dispose. No chart logic.
 *
 * Only the pieces the workspace draws are registered, so the bundle carries
 * a candlestick/line/bar chart rather than all of ECharts.
 *
 * A canvas is opaque to a screen reader, so the caller supplies `ariaLabel`
 * and the evidence panel carries the same numbers as text.
 */
import { BarChart, CandlestickChart, CustomChart, LineChart } from "echarts/charts";
import {
	AxisPointerComponent,
	DataZoomComponent,
	GridComponent,
	LegendComponent,
	MarkAreaComponent,
	TooltipComponent,
} from "echarts/components";
import * as echarts from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import type { EChartsOption } from "echarts";
import { useEffect, useRef } from "react";

echarts.use([
	CandlestickChart,
	LineChart,
	BarChart,
	// The net-value profile strip is drawn with renderItem.
	CustomChart,
	GridComponent,
	TooltipComponent,
	LegendComponent,
	DataZoomComponent,
	MarkAreaComponent,
	AxisPointerComponent,
	CanvasRenderer,
]);

interface EChartProps {
	option: EChartsOption;
	ariaLabel: string;
	className?: string;
}

export function EChart({ option, ariaLabel, className }: EChartProps) {
	const container = useRef<HTMLDivElement>(null);
	const instance = useRef<echarts.ECharts | null>(null);

	useEffect(() => {
		if (!container.current) return;
		const chart = echarts.init(container.current, undefined, { renderer: "canvas" });
		instance.current = chart;
		const observer = new ResizeObserver(() => chart.resize());
		observer.observe(container.current);
		return () => {
			observer.disconnect();
			chart.dispose();
			instance.current = null;
		};
	}, []);

	useEffect(() => {
		// notMerge: switching instrument must not leave the previous series behind.
		instance.current?.setOption(option, { notMerge: true });
	}, [option]);

	return <div ref={container} className={className} role="img" aria-label={ariaLabel} />;
}
